import { useState, useContext, useCallback } from 'react';
import Router, { useRouter } from 'next/router';
import styled from 'styled-components';

import BackButton from 'components/Button/BackButton';
import Dropdown from 'components/Navigation/Dropdown';
import Tabs from 'components/Navigation/Tabs';

import { AccountContext } from 'context/AccountProvider';
import { useClaysLocation } from 'context/ClaysLocationProvider';

import BREAKPOINTS from 'styles/breakpoints';
import COLORS from 'styles/colors';
import { DotsPattern } from 'styles/global';
import TYPO, { rem } from 'styles/typography';

import signOut from 'utils/authentication/sign-out';

import BgCircleMask from 'public/images/backgrounds/bg-circle-mask.svg';

const ACCOUNT_NAV_ITEMS = [
  { label: 'My Sessions', value: '/my-clays' },
  { label: 'My Album', value: '/my-clays/my-album' },
  { label: 'My Game Summary', value: '/my-clays/my-game-summary' },
  { label: 'My Details', value: '/my-clays/profile' },
  { label: 'Sign out', value: 'sign-out' },
];

const Container = styled.div`
  ${DotsPattern}
  padding-bottom: 50px;
  background-color: ${COLORS.forest};
  color: ${COLORS.pinkMuted};
  overflow: hidden;
  background-size: 33px auto;
  min-height: calc(100vh - 361px);

  ${BREAKPOINTS.medium`
    padding-bottom: 120px;
    background-size: 63px auto;
    min-height: calc(100vh - 232px);
  `}
`;

const Header = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 430px;

  ${BREAKPOINTS.medium`
    padding-top: 120px;
    min-height: 388px;
    box-shadow: inset 0px 120px 0px 0px ${COLORS.pinkMuted};
  `}
`;

const CircleMask = styled.div`
  clip-path: url(#bg-circle-mask);
  position: absolute;
  left: 50%;
  width: 100%;
  min-width: 1440px;
  min-height: inherit;
  background-color: ${COLORS.pinkMuted};

  /* Fix clip-path in iOS Safari - translateZ(0) */
  transform: translateZ(0) translateX(-50%);
`;

const SvgClipPath = styled(BgCircleMask)`
  position: absolute;
  top: 0;
  left: 0;
`;

const MobileNav = styled(Dropdown)`
  position: absolute;
  top: 95px;
  z-index: 20;

  ${BREAKPOINTS.medium`
    display: none;
  `}
`;

const DesktopNav = styled(Tabs)`
  display: none;
  position: absolute;
  top: 110px;
  z-index: 30;

  ${BREAKPOINTS.medium`
    display: flex;
  `}
`;

const BackButtonStyled = styled(BackButton)`
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 50;

  ${BREAKPOINTS.medium`
    top: 180px;
    left: 40px;
  `}
`;

const AvatarContainer = styled.div`
  position: relative;
  transform: translateY(36px);
`;

const Avatar = styled.div`
  margin: 0 auto 11px;
  width: 110px;
  height: 110px;
  border: 3px solid ${COLORS.white};
  border-radius: 50%;
  background: ${COLORS.forest}
    ${({ imgUrl }) => (imgUrl ? `url(${imgUrl})` : 'none')} no-repeat center;
  background-size: cover;
  box-shadow: 0px 4px 4px ${COLORS.black.opacity(0.25)};
`;

const Username = styled.span`
  ${TYPO.wulkan}
  display: block;
  font-size: ${rem(30)};
  line-height: ${rem(36)};
  text-align: center;
`;

const Content = styled.div`
  margin-top: -60px;
  position: relative;

  ${BREAKPOINTS.medium`
    margin-top: 0;
  `}
`;

const PageLayout = ({ hasBackBtn = false, className, children }) => {
  const router = useRouter();
  const { user, setUser } = useContext(AccountContext);
  const { claysLocation } = useClaysLocation();

  const topLevelPath = router.pathname.split('/[id]')[0];
  const [activeAccountItem, setActiveAccountItem] = useState(topLevelPath);

  const handleRouteChange = useCallback(
    (value) => {
      if (value === 'sign-out') {
        signOut(claysLocation);
        setUser(null);
      } else {
        setActiveAccountItem(value);
        Router.push(value);
      }
    },
    [setUser, claysLocation]
  );

  const desktopNavItems = ACCOUNT_NAV_ITEMS.filter(
    ({ value }) => value !== 'sign-out'
  );

  return (
    <Container className={className}>
      <Header>
        <CircleMask />
        <SvgClipPath />

        <MobileNav
          items={ACCOUNT_NAV_ITEMS}
          activeItem={activeAccountItem}
          onClick={(value) => handleRouteChange(value)}
        />

        <DesktopNav items={desktopNavItems} activePath={topLevelPath} />

        {hasBackBtn && <BackButtonStyled />}

        <AvatarContainer>
          <Avatar imgUrl={user?.avatar} />
          <Username>{user?.firstName}</Username>
        </AvatarContainer>
      </Header>
      <Content>{children}</Content>
    </Container>
  );
};

export default PageLayout;
